import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Settings,
  Sparkles,
  CheckCircle2,
  Loader2,
  XCircle
} from 'lucide-react';

export type GenerationStage =
  | 'preparing'
  | 'generating'
  | 'finalizing'
  | 'complete'
  | 'error'
  // Legacy stages for backward compatibility
  | 'analyzing'
  | 'processing'
  | 'applying';

interface GenerationProgressModalProps {
  isOpen: boolean;
  currentStage: GenerationStage;
  sketchCount: number;
  error?: string;
  onClose?: () => void;
}

interface StageConfig {
  id: 'preparing' | 'generating' | 'finalizing';
  icon: React.FC<{ className?: string }>;
  title: string;
}

const stages: StageConfig[] = [
  {
    id: 'preparing',
    icon: Settings,
    title: 'Preparing'
  },
  {
    id: 'generating',
    icon: Sparkles,
    title: 'Generating'
  },
  {
    id: 'finalizing',
    icon: CheckCircle2,
    title: 'Finalizing'
  }
];

export const GenerationProgressModal: React.FC<GenerationProgressModalProps> = ({
  isOpen,
  currentStage,
  sketchCount,
  error,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay for animation
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      setTimeout(() => setShouldRender(false), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    // Auto-close after completion
    if (currentStage === 'complete' && onClose) {
      const timer = setTimeout(onClose, 2000); // Increased to 2 seconds to show complete state
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [currentStage, onClose]);

  if (!shouldRender) return null;

  // Map old stages to new simplified stages for backward compatibility
  const mapStageToSimplified = (stage: GenerationStage): 'preparing' | 'generating' | 'finalizing' | 'complete' | 'error' => {
    if (stage === 'analyzing' || stage === 'processing') return 'preparing';
    if (stage === 'applying') return 'finalizing';
    if (stage === 'complete') return 'complete';
    if (stage === 'error') return 'error';
    // Default to generating for any other stage
    return 'generating';
  };

  const simplifiedStage = mapStageToSimplified(currentStage);

  // Calculate the current stage index for progress display
  const getCurrentStageIndex = () => {
    if (currentStage === 'complete') {
      // All stages are complete
      return stages.length;
    }
    if (currentStage === 'error') {
      // Find which stage we were on when error occurred
      return stages.findIndex(s => s.id === simplifiedStage);
    }
    // Find the current active stage
    return stages.findIndex(s => s.id === simplifiedStage);
  };

  const currentStageIndex = getCurrentStageIndex();

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center transition-all duration-300',
        isVisible ? 'opacity-100' : 'opacity-0'
      )}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={currentStage === 'complete' || currentStage === 'error' ? onClose : undefined}
      />

      {/* Modal */}
      <div
        className={cn(
          'relative bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 transition-all duration-300',
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        )}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Generating Components
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Processing {sketchCount} {sketchCount === 1 ? 'sketch' : 'sketches'}
          </p>
        </div>

        {/* Horizontal Timeline */}
        <div className="relative px-8">
          {/* Progress Line Background */}
          <div className="absolute top-8 left-12 right-12 h-0.5 bg-gray-200 dark:bg-gray-700" />

          {/* Active Progress Line */}
          <div
            className="absolute top-8 left-12 h-0.5 bg-blue-500 dark:bg-blue-400 transition-all duration-700 ease-out"
            style={{
              width: (() => {
                if (currentStage === 'complete') {
                  return 'calc(100% - 96px)'; // Full width minus padding
                }
                const activeIndex = stages.findIndex(s => s.id === simplifiedStage);
                if (activeIndex === -1) return '0%';
                return `calc(${(activeIndex / (stages.length - 1)) * 100}% * (100% - 96px) / 100%)`;
              })()
            }}
          />

          {/* Stage Items */}
          <div className="relative flex justify-between">
            {stages.map((stage, index) => {
              const Icon = stage.icon;

              // Determine the state of each stage
              const isComplete = currentStage === 'complete' ||
                               (currentStage !== 'error' && index < stages.findIndex(s => s.id === simplifiedStage));

              const isActive = currentStage !== 'complete' &&
                              currentStage !== 'error' &&
                              stage.id === simplifiedStage;

              const showError = currentStage === 'error' && stage.id === simplifiedStage;

              return (
                <div key={stage.id} className="flex flex-col items-center">
                  {/* Icon Container */}
                  <div className="relative">
                    <div
                      className={cn(
                        'w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300',
                        isComplete && 'bg-blue-500 dark:bg-blue-600',
                        isActive && 'bg-blue-500 dark:bg-blue-600',
                        showError && 'bg-red-500 dark:bg-red-600',
                        !isComplete && !isActive && !showError && 'bg-gray-200 dark:bg-gray-700'
                      )}
                    >
                      {isActive && !showError ? (
                        <Loader2 className="w-7 h-7 text-white animate-spin" />
                      ) : showError ? (
                        <XCircle className="w-7 h-7 text-white" />
                      ) : (
                        <Icon
                          className={cn(
                            "w-7 h-7 transition-colors duration-300",
                            (isComplete || isActive) ? 'text-white' : 'text-gray-400 dark:text-gray-500'
                          )}
                        />
                      )}
                    </div>
                  </div>

                  {/* Label */}
                  <span
                    className={cn(
                      'mt-3 text-sm font-medium transition-colors duration-300',
                      isComplete && 'text-blue-600 dark:text-blue-400',
                      isActive && 'text-blue-600 dark:text-blue-400',
                      showError && 'text-red-600 dark:text-red-400',
                      !isComplete && !isActive && !showError && 'text-gray-400 dark:text-gray-500'
                    )}
                  >
                    {stage.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Error Message */}
        {currentStage === 'error' && error && (
          <div className="mt-6 p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
            <p className="text-sm text-red-600 dark:text-red-400 text-center">{error}</p>
          </div>
        )}

        {/* Error Actions */}
        {currentStage === 'error' && onClose && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
